import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { COMMONTYPES } from 'TYPES/event.types';
import { Button, EmptyCon } from 'components/atoms';
import { AddressCard, EditAddressModal } from 'components/molecules';
import { deleteAddress, getAllAddress } from 'network/services/address';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useRecoilState } from 'recoil';
import { contactState } from 'store/atoms/contactAtom';
import { DeliveryCON } from './styles';

// Enhanced styling for address grid and components
const AddressGridContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const AddressPageHeader = styled.div`
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

const AddButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 24px;

  button {
    width: 100%;
    max-width: 472px;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    margin-top: 16px;
  }
`;

export const DeliveryAddress = () => {
  const [contact, setcontactInfo] = useRecoilState(contactState);
  const { data, isFetchedAfterMount, refetch } = useQuery('addresses', getAllAddress);
  const addresses = data?.data ? data.data : [];

  const [deletingId, setDeletingId] = React.useState<string | number | null>(null);

  const { mutateAsync: removeAddress, isLoading: deletingAddress } = useMutation({
    mutationFn: (id: string) => deleteAddress(String(id)),
    onSuccess: () => {
      enqueueSnackbar('Address deleted', { variant: 'success' });
      refetch();
    },
    onError: (err: any) => {
      console.error('Delete address error:', err);
      enqueueSnackbar('Failed to delete address', { variant: 'error' });
    }
  });

  const handleDelete = async (id: string | number) => {
    // simple confirmation
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Are you sure you want to delete this address?');
    if (!ok) return;
    try {
      setDeletingId(id);
      await removeAddress(String(id));
    } finally {
      setDeletingId(null);
    }
  };

  const onAddNew = (e: COMMONTYPES['buttonClick']) => {
    e.preventDefault();
    setcontactInfo(oldCred => ({ ...oldCred, operation: 'add', isEditAddressModal: true }));
  };

  return (
    <DeliveryCON>
      <AddressPageHeader>Delivery Address</AddressPageHeader>

      {!isFetchedAfterMount ? (
        <EmptyCon>
          <CircularProgress size={30} />
        </EmptyCon>
      ) : addresses.length > 0 ? (
        <AddressGridContainer>
          {addresses.map(address => (
            <AddressCard
              key={address.id}
              address={address}
              onDelete={() => handleDelete(address.id)}
              deleting={deletingAddress && deletingId === address.id}
            />
          ))}
        </AddressGridContainer>
      ) : (
        <EmptyCon>No addresses yet.</EmptyCon>
      )}

      <AddButtonContainer>
        <Button onClick={onAddNew}>Add New Address</Button>
      </AddButtonContainer>

      {contact.isEditAddressModal && <EditAddressModal refetch={refetch} />}
    </DeliveryCON>
  );
};